const dataAccessMethods = require('../../../../dataAccessMethods');
const { config, moduleRegistration } = require('@sstdev/lib_metadata-config');
const { merge } = require('lodash');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP =
    'Lookup record by a single property,(possibly always on the server). Select it for edit if found, prepare new record if not found. (E.g. VIN lookup).';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBloc
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;

    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);

    line1OfInputs
        .appendField('  Namespace:')
        .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
        .appendField('  Relation:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)), 'relation')
        .appendField('  Property Name:')
        .appendField(new Blockly.FieldTextInput('title'), 'propertyName')
        .appendField('  Validation Pattern:')
        .appendField(new Blockly.FieldTextInput(''), 'validationPattern');

    // Load possible actions and default action
    let foundOneDropdown = new Blockly.FieldDropdown(config.actionElementActions);
    foundOneDropdown.setValue('edit');
    let noneFoundDropdown = new Blockly.FieldDropdown(config.actionElementActions);
    noneFoundDropdown.setValue('new');
    //line 2 of inputs
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Found One Action:')
        .appendField(foundOneDropdown, 'foundOneAction')
        .appendField('  None Found Action:')
        .appendField(noneFoundDropdown, 'noneFoundAction')
        .appendField('  Remote Lookup:')
        .appendField(new Blockly.FieldCheckbox('false'), 'remoteLookup')
        // In the future, we may want to make this more flexible by allowing the user to specify a required selection other than location.
        .appendField(' Disable until location selected:')
        .appendField(new Blockly.FieldCheckbox('false'), 'disableUntilLocationSelected');

    blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Display Scan Button')
        .appendField(new Blockly.FieldCheckbox(), 'displayScanButton')
        .appendField('  Display RFID Power Button')
        .appendField(new Blockly.FieldCheckbox(), 'displayRfidPowerButton')
        .appendField('  Scan RFID')
        .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
        .appendField('  Scan Barcode')
        .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation', 'propertyName', { key: 'pattern', value: 'validationPattern' }]);

    //action
    addCodeProperties(['foundOneAction', 'noneFoundAction', { key: 'remoteLookup', handler: convertToBoolean }]);

    //disabled
    addCodeProperties([{ key: 'disableUntilLocationSelected', handler: convertToBoolean }]);

    // filters
    addCodeProperties([{ key: 'style', value: 'buttonStyle' }, 'iconName']);
    addCodeProperties([
        { key: 'showBusyUntilFinished', handler: convertToBoolean },
        { key: 'displayScanButton', handler: convertToBoolean },
        { key: 'displayRfidPowerButton', handler: convertToBoolean },
        { key: 'scanRfid', handler: convertToBoolean },
        { key: 'scanBarcode', handler: convertToBoolean }
    ]);
    code.filters = Blockly.JavaScript.statementToCode(blocklyBlock, 'filters');
    return code;
}

/**
 * Builds relation metadata for the FindRecord action.
 * This is a search/lookup action block that finds records for editing.
 * It should NOT define canonical relation schemas - those come from Forms.
 * This block only propagates filter contributions without overwriting canonical required/constraints.
 */
function asDataDictionary(hNode, context) {
    const { namespace, relation, filters = [] } = hNode;

    // Collect contributions from filters
    const filterGroup = moduleRegistration.getHNodeTypeGroup('filters');

    let mergedSubDD = {};
    let mergedPartialMap = {};

    for (const lc of filters) {
        const [cSub = {}, cPartial = {}] =
            (filterGroup && typeof filterGroup.asDataDictionary === 'function'
                ? filterGroup.asDataDictionary(lc, { ...context, namespace, relation })
                : [{}, {}]) || [{}, {}];

        mergedSubDD = merge(mergedSubDD, cSub);
        mergedPartialMap = Object.assign(mergedPartialMap, cPartial);
    }

    // View-only block: only pass through filter subDataDictionary contributions.
    // Do NOT create or merge into the canonical [namespace][relation] slot.
    // Canonical schemas are defined by Form blocks.
    return [mergedSubDD, mergedPartialMap];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
